// Shared time-range filter model used by every management data table.
//
// A range is stored as { preset, from, to }. For a named preset only `preset`
// is meaningful — the bounds are derived from "now" every time the range is
// read, so a tab left open across midnight keeps meaning "last 6 months"
// instead of freezing to the day the bundle loaded. `from`/`to` hold calendar
// days in `yyyy-MM-dd` (what <input type="date"> speaks) and are populated only
// for the 'custom' preset.
//
// `resolveDateRangeParam()` converts the resolved calendar days into absolute
// UTC instants (start-of-day / end-of-day in the *browser's* timezone) right
// before the query goes out, so the server never has to guess the user's zone.

export type TimeRangePresetId = '30d' | '3m' | '6m' | '12m' | 'ytd' | 'all' | 'custom';

export interface TimeRangeValue {
  preset: TimeRangePresetId;
  from: string;
  to: string;
}

export interface DateRangeParam {
  field: string;
  from?: string;
  to?: string;
}

export interface ResolvedTimeRange {
  from: Date | null;
  to: Date | null;
}

interface TimeRangePreset {
  id: Exclude<TimeRangePresetId, 'custom'>;
  label: string;
  // null = open-ended (all time)
  from: (now: Date) => Date | null;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const daysAgo = (now: Date, n: number) => {
  const d = startOfDay(now);
  d.setDate(d.getDate() - n);
  return d;
};

// setMonth() overflows on short months (Aug 30 minus 6 becomes Mar 2), which
// would silently widen the window. Clamp to the last day of the target month.
const monthsAgo = (now: Date, n: number) => {
  const d = startOfDay(now);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDayOfMonth));
  return d;
};

export const TIME_RANGE_PRESETS: TimeRangePreset[] = [
  { id: '30d', label: 'Last 30 days', from: (now) => daysAgo(now, 30) },
  { id: '3m', label: 'Last 3 months', from: (now) => monthsAgo(now, 3) },
  { id: '6m', label: 'Last 6 months', from: (now) => monthsAgo(now, 6) },
  { id: '12m', label: 'Last 12 months', from: (now) => monthsAgo(now, 12) },
  { id: 'ytd', label: 'Year to date', from: (now) => new Date(now.getFullYear(), 0, 1) },
  { id: 'all', label: 'All time', from: () => null },
];

export const toDayString = (d: Date): string => [
  d.getFullYear(),
  String(d.getMonth() + 1).padStart(2, '0'),
  String(d.getDate()).padStart(2, '0'),
].join('-');

// yyyy-MM-dd -> local Date. `new Date('2026-08-30')` would parse as UTC midnight
// and shift the day backwards for anyone west of Greenwich, so parse the parts.
export const fromDayString = (value: string): Date | null => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!parts) {
    return null;
  }
  const date = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

// Named presets carry no stored bounds — see the file header.
export const buildTimeRange = (
  preset: Exclude<TimeRangePresetId, 'custom'>,
): TimeRangeValue => ({ preset, from: '', to: '' });

// `from`/`to` are yyyy-MM-dd, so a lexicographic compare is chronological.
//
// min/max on <input type="date"> only mark a value invalid — a typed date still
// commits and fires change — so crossed bounds are normalized here rather than
// left to return zero rows under an empty state that says "widen the range".
// Clearing both bounds collapses to All time, so the button label, the chip and
// the empty state agree with the rows actually coming back.
export const buildCustomTimeRange = (from: string, to: string): TimeRangeValue => {
  if (!from && !to) {
    return { preset: 'all', from: '', to: '' };
  }
  if (from && to && from > to) {
    return { preset: 'custom', from: to, to: from };
  }
  return { preset: 'custom', from, to };
};

// Default for transactional tables (orders, invoices, receivings, …).
export const DEFAULT_TIME_RANGE_PRESET: Exclude<TimeRangePresetId, 'custom'> = '6m';
// Default for catalog/reference tables (products, vendors, …), where rows
// predate the createdAt/createdDate fields and a 6-month window would hide them.
export const ALL_TIME_RANGE_PRESET: Exclude<TimeRangePresetId, 'custom'> = 'all';

// The only place a range turns into concrete days. Everything that needs
// bounds — the chip, the custom inputs, the query param — goes through here so
// they can never disagree.
export const resolveTimeRange = (
  value: TimeRangeValue | undefined,
  now: Date = new Date(),
): ResolvedTimeRange => {
  if (!value) {
    return { from: null, to: null };
  }

  if (value.preset === 'custom') {
    return { from: fromDayString(value.from), to: fromDayString(value.to) };
  }

  const preset = TIME_RANGE_PRESETS.find((p) => p.id === value.preset);
  const from = preset ? preset.from(now) : null;
  return { from, to: from ? startOfDay(now) : null };
};

export const isAllTime = (value: TimeRangeValue | undefined) => {
  const { from, to } = resolveTimeRange(value);
  return !from && !to;
};

export const getTimeRangeLabel = (value: TimeRangeValue): string => {
  if (value.preset === 'custom') {
    return 'Custom range';
  }
  return TIME_RANGE_PRESETS.find((p) => p.id === value.preset)?.label ?? 'Custom range';
};

// Lower-cased preset label for prose ("…reset it to the last 6 months.").
export const getTimeRangePhrase = (preset: TimeRangePresetId): string => (
  TIME_RANGE_PRESETS.find((p) => p.id === preset)?.label.toLowerCase() ?? 'the default range'
);

// The value handed to the API layer. Returns undefined when the range is
// open-ended so the request body stays clean and RTK Query cache keys don't
// churn on a no-op filter.
export const resolveDateRangeParam = (
  field: string | undefined,
  value: TimeRangeValue | undefined,
): DateRangeParam | undefined => {
  if (!field || !value) {
    return undefined;
  }

  const { from, to } = resolveTimeRange(value);
  if (!from && !to) {
    return undefined;
  }

  const param: DateRangeParam = { field };
  if (from) {
    param.from = from.toISOString();
  }
  if (to) {
    // inclusive end of the selected day, in the browser's timezone
    param.to = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999).toISOString();
  }
  return param;
};

// Flat query-string form, for the CSV export endpoints (which take GET params
// rather than a JSON body). Keys mirror what the server reassembles.
export const toDateRangeQueryParams = (
  param: DateRangeParam | undefined,
): Record<string, string> => {
  if (!param) {
    return {};
  }
  return {
    dateRangeField: param.field,
    ...(param.from ? { dateRangeFrom: param.from } : {}),
    ...(param.to ? { dateRangeTo: param.to } : {}),
  };
};
